import { z } from 'zod'

/**
 * How the analytics build makes a sub-account's stream — the settings analytics-dashboard-backend
 * reads from `headline.streams` (its #58): the sources (one stored mode), how reach is estimated,
 * which broadcast clips are taken, and the rollup. Headline stores them and builds nothing from
 * them; the stream's config schema (stream.ts) carries them.
 */

/**
 * How the analytics cache builds a stream: `reports` — what the workspace's reports delivered,
 * the curated read — `semantic` — live matching of the stream's topics, the read the legacy
 * webapp's live tier has — or `both`, the union of the two (the MV_PLUS_REPORT equivalent): the
 * successor of the cache job's hardcoded live list. Configure shows it as two toggles. Headline
 * serves every mode from the job's cached payload and never reads the warehouse for a stream, so
 * nothing here branches on it.
 */
export const STREAM_MODES = ['reports', 'semantic', 'both'] as const
export const streamModeSchema = z.enum(STREAM_MODES)
export type StreamMode = z.infer<typeof streamModeSchema>

/** The mode as its two sources: report data, the semantic stream, or both. Never neither. */
export function streamModeSources(mode: StreamMode): { reports: boolean; semantic: boolean } {
  return { reports: mode !== 'semantic', semantic: mode !== 'reports' }
}

/** The mode two sources make; null when neither is on, which no stream may be. */
export function streamModeOf(sources: { reports: boolean; semantic: boolean }): StreamMode | null {
  if (sources.reports && sources.semantic) return 'both'
  if (sources.reports) return 'reports'
  return sources.semantic ? 'semantic' : null
}

/**
 * How the analytics build estimates an article's reach (`streams.reach_model`). Null is the
 * build's own default for the client — today several get theirs from lists in
 * analytics-dashboard-backend (article-level reach for some, outlet traffic for others), so null
 * is never written unasked: `none` would switch a client's reach off.
 */
export const STREAM_REACH_MODELS = [
  'none',
  'domain-traffic',
  'clipbook-article-level-reach',
] as const
export const streamReachModelSchema = z.enum(STREAM_REACH_MODELS)
export type StreamReachModel = z.infer<typeof streamReachModelSchema>
export const STREAM_REACH_MODEL_LABELS: Record<StreamReachModel, string> = {
  none: 'Off',
  'domain-traffic': 'Outlet traffic',
  'clipbook-article-level-reach': 'Clipbook article-level reach',
}

/** Which broadcast clips the build takes (`streams.broadcast_source`); null is the client's
 *  default, as for reach. */
export const STREAM_BROADCAST_SOURCES = ['reports', 'both', 'full_corpus'] as const
export const streamBroadcastSourceSchema = z.enum(STREAM_BROADCAST_SOURCES)
export type StreamBroadcastSource = z.infer<typeof streamBroadcastSourceSchema>
export const STREAM_BROADCAST_SOURCE_LABELS: Record<StreamBroadcastSource, string> = {
  reports: 'Report-attached clips',
  both: 'Report-attached clips plus semantic matches',
  full_corpus: 'All clips (unfiltered)',
}

/** The label of a build setting left to the build: what the client has today. */
export const STREAM_BUILD_DEFAULT_LABEL = 'Default (what the client has today)'

/** At most this many sub-accounts rolled up into one stream. */
export const STREAM_ROLLUP_MAX = 50
